// Контракт ручки «где дыры у ученика»: `GET /v1/students/:userId/skills`.
//
// ⚠️ **Здесь только СЛОВА, а не словари.** Диагноз считает сервер целиком, и
// клиенту уезжает уже готовая строка: название темы, класс, два числа, `label` и
// `remedy` сработавшей ошибки, текст `why` у ребра спуска. Причина в 1.4
// `docs/student-errors-design.md`: панель живёт в карточке ученика, а карточка
// это НЕ ленивый чанк, и затащить туда 54 узла, 89 рёбер и 29 рецептов значит
// утяжелить стартовый чанк всем, включая учеников, ради одного экрана одной роли.
// Словарь `MISCONCEPTIONS` в бандл не едет ни одним путём, и это проверяется
// поиском самого слова в собранных чанках (сценарий 52 docs/homework-acceptance.md).
//
// ⚠️ Файл лежит в КОРНЕВОМ экспорте и обязан оставаться пустым от значений:
// схемы zod и типы, ноль импортов из `./oge/*`. Один импорт значением оттуда
// вытащил бы граф в тот самый стартовый чанк, ради которого ручка и заведена.
//
// ⚠️ Форма предметно-нейтральна (3.3 проекта): слаги это свободные строки,
// `subject` обязателен с первой версии, а подпись блока спуска приезжает полем
// `rootWord`, а не литералом в компоненте. У русского спуск тот же, но слово
// «корень» там запрещено (преподаватель русского читает его как морфему).

import { z } from "zod";

/**
 * Сработавшая ошибка вместе с рецептом.
 *
 * `label` и `remedy` отдаёт сервер, а не берёт клиент из словаря по `id`: `id`
 * здесь для того, чтобы клиент мог отличить две строки друг от друга и свернуть
 * повтор, а не для похода в справочник, которого у него нет.
 */
export const StudentSkillMisconception = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  remedy: z.string().min(1),
  /** На скольких заданиях окна она сработала. Знаменатель берётся у строки навыка. */
  tasks: z.number().int().min(1),
});
export type StudentSkillMisconception = z.infer<typeof StudentSkillMisconception>;

/**
 * Строка навыка: вердикт вместе со своим знаменателем.
 *
 * ⚠️ `failed` это булево, а не состояние словом. Три состояния экрана
 * («провален», «шатко», «не измеряли») выводятся клиентом из `tasks`, `wrong` и
 * `failed`: enum пришлось бы расширять при каждой правке порога, а порог это
 * первая величина проекта, которую будут крутить по живым данным.
 *
 * ⚠️ Два числа, а не доля. «0 из 0» и «0 из 40» это разные сообщения, а процент
 * их уравнивает (тот же довод, что у `StudentProgress` в `./invite.ts`).
 */
export const StudentSkillRow = z.object({
  skill: z.string().min(1),
  /**
   * Название темы. У слага, которого в текущем словаре уже нет, здесь стоит сам
   * слаг: строка, записанная прошлой выкаткой, обязана доехать числами, а не
   * исчезнуть. По той же причине ниже допустим `null`.
   */
  name: z.string().min(1),
  section: z.string().min(1).nullable(),
  sectionName: z.string().min(1).nullable(),
  /** Класс программы. «5-6 класс» рядом с «8 класс» это и есть глубина пробела. */
  grade: z.string().min(1).nullable(),
  /** Сколько заданий вошло в окно. Знаменатель строки «неверно N из M». */
  tasks: z.number().int().min(0),
  wrong: z.number().int().min(0),
  failed: z.boolean(),
  /**
   * Надёжность это РАЗМЕР ВЫБОРКИ, а не глубина провала. Стоит рядом с числами
   * затем, чтобы «не берётся, неверно 1 из 1» читалось как предположение, а не
   * как приговор.
   */
  confidence: z.enum(["weak", "medium", "strong"]),
  /**
   * Сколько раз ошибка ЭТОЙ темы сработала в заданиях ДРУГИХ тем.
   *
   * ⚠️ В `tasks` и `wrong` эти срабатывания не входят: знаменатель считается по
   * собственным заданиям темы и только по ним. Поле нужно экрану ровно затем,
   * чтобы строка «неверно 0 из 0» с пометкой провала не выглядела поломкой:
   * сказать надо «заданий по этой теме не давали, но ошибка встретилась в других
   * темах N раз» (12.6 docs/student-errors-design.md).
   */
  foreignHits: z.number().int().min(0),
  misconceptions: z.array(StudentSkillMisconception),
});
export type StudentSkillRow = z.infer<typeof StudentSkillRow>;

/**
 * Прямой пререквизит корня: то, что лежит ГЛУБЖЕ нашего измерения.
 *
 * Блок «глубже мы не проверяли» обязателен и не является отладкой: у семи тем
 * дна графа генераторов нет ни у одной, то есть настоящий корень почти всегда
 * ниже того, что мы умеем спросить (3.2 проекта). Преподавателю это даёт готовый
 * вопрос на урок вместо обещания.
 */
export const StudentSkillBelow = z.object({
  skill: z.string().min(1),
  name: z.string().min(1),
  grade: z.string().min(1).nullable(),
  /** `hard`: без этого тема не берётся вовсе. `soft`: берётся, но тяжело. */
  kind: z.enum(["hard", "soft"]),
  /** Текст ребра, слово в слово из графа: он написан для преподавателя. */
  why: z.string().min(1),
  /**
   * Есть ли у платформы задания по этой теме.
   *
   * ⚠️ Ровно от этого поля зависит, будет ли у строки кнопка. Кнопка, ведущая в
   * отказ «генераторов нет», хуже отсутствия кнопки.
   */
  covered: z.boolean(),
});
export type StudentSkillBelow = z.infer<typeof StudentSkillBelow>;

/**
 * Корень: самая глубокая проваленная тема, до которой дотянулся спуск.
 *
 * ⚠️ `from` это СЛАГИ, а не названия, и словаря клиенту для них не нужно: каждый
 * слаг оттуда это проваленная тема, то есть строка, которая уже лежит в `skills`
 * со своим названием. Фразу «не берётся X, потому что не Y» клиент собирает сам
 * по шаблону предмета: у русского шаблон другой, и держать склонения в двух
 * местах хуже, чем один шаблон на предмет.
 *
 * Пустой `from` означает, что корень провален сам по себе и объяснять нечего.
 */
export const StudentSkillRootRow = z.object({
  skill: z.string().min(1),
  from: z.array(z.string().min(1)),
  /**
   * Числа самого корня. Те же, что в его строке `skills`: блок корня рисуется
   * отдельно от списка, и заставлять его сшивать две части ответа ради двух
   * чисел значит платить сложностью клиента за экономию двадцати байт.
   */
  tasks: z.number().int().min(0),
  wrong: z.number().int().min(0),
  confidence: z.enum(["weak", "medium", "strong"]),
  /**
   * Корень держится ТОЛЬКО на чужом доказательстве: своих заданий по нему не
   * давали либо они на провал не набрали, а ошибка этой темы сработала в
   * заданиях других тем. Экран обязан сказать это отдельной фразой: числа у
   * такого корня нулевые честно, и без объяснения они читаются как поломка.
   */
  foreignOnly: z.boolean(),
  below: z.array(StudentSkillBelow),
});
export type StudentSkillRootRow = z.infer<typeof StudentSkillRootRow>;

/** Тема, по которой генераторы есть, а заданий ученику не давали. */
export const StudentSkillTopic = z.object({
  skill: z.string().min(1),
  name: z.string().min(1),
  /**
   * Сколько у темы покрытых генераторами предков по жёстким рёбрам, считая
   * ЗАМЫКАНИЕ, а не прямые рёбра.
   *
   * ⚠️ Ноль означает «лист выродится в лист по самой теме», то есть спуска не
   * будет. Без этого числа список холодного старта ровный, и преподаватель не
   * отличает тему, по которой лист даст настоящий корень, от той, по которой он
   * даст только её саму. Считает сервер: нужен граф, а граф в браузер не едет.
   */
  coveredAncestors: z.number().int().min(0),
});
export type StudentSkillTopic = z.infer<typeof StudentSkillTopic>;

export const StudentSkillsResponse = z.object({
  /** Предмет, сегодня всегда `oge-math`. Обязателен с первой версии (3.3). */
  subject: z.string().min(1),
  /**
   * Как называть найденное место на экране: «корень» у математики, «причина» у
   * русского. Данными, а не литералом в компоненте, потому что потом это правка
   * контракта, а не строки.
   */
  rootWord: z.string().min(1),
  /** Окно вердикта: по скольким последним заданиям и за сколько суток. */
  window: z.object({
    days: z.number().int().min(1),
    tasks: z.number().int().min(1),
  }),
  /** Сколько заданий вошло в счёт. Подпись шапки: «посчитано по 34 заданиям». */
  measured: z.number().int().min(0),
  /**
   * ⚠️ Навык, по которому заданий нет вовсе, строки НЕ получает. Пустота
   * читается как «тут всё хорошо», поэтому непроверенное живёт отдельным полем
   * `unmeasured`, а не выводится из отсутствия строки.
   */
  skills: z.array(StudentSkillRow),
  roots: z.array(StudentSkillRootRow),
  unmeasured: z.array(StudentSkillTopic),
});
export type StudentSkillsResponse = z.infer<typeof StudentSkillsResponse>;
